import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import moment from 'moment';
import { Configuration } from 'src/app/app.constants';
import { DocumentTypes } from 'src/app/modules/shared/enums/documentTypes';
import { UploadTypes } from 'src/app/modules/shared/enums/uploadTypes';
import { UrlService } from 'src/app/modules/shared/services/url.service';
import { AuthenticationService } from 'src/app/modules/shared/services/authentication.service';
import { ExternalAuthService } from 'src/app/modules/external/services/external-auth.service';
import { DocumentViewModel } from 'src/app/modules/shared/viewModels/documents/documentViewModel';
import { UploadViewModel } from 'src/app/modules/shared/viewModels/documents/uploadViewModel';
import { AttachmentsUtilities } from '../../../utilities/attachments.utilities';
import { T } from 'src/assets/i18n/translation-keys';

@Component({
    selector: 'app-attachment-card',
    templateUrl: 'attachment-card.component.html',
    styleUrls: ['attachment-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AttachmentCardComponent implements OnInit {
  @Output() onAttachmentLinkClicked = new EventEmitter<DocumentViewModel>();
  @Output() onaAttachmentDelete = new EventEmitter<DocumentViewModel>();
  @Output() onAttachmentDownload = new EventEmitter<DocumentViewModel>();
  @Input() attachment: DocumentViewModel;
  @Input() uploadViewModel: UploadViewModel;
  @Input() accountId: number;
  @Input() allDocuments: DocumentViewModel[] = [];
  @Input() contextualInfo: any;
  public attachmentCreatedDate: string = '';
  public attachmentType: DocumentTypes;
  public attachmentTypes = DocumentTypes;
  public readonly T = T;

  constructor(
    private readonly urlService: UrlService,
    private readonly configuration: Configuration,
    private readonly authenticationService: AuthenticationService,
    private readonly externalAuthService: ExternalAuthService
  ) {}

  ngOnInit() {
    this.attachmentCreatedDate = moment(this.attachment.created).format('DD MMM, YYYY HH:mm');
    this.attachmentType = AttachmentsUtilities.getDocumentType(this.attachment).type;
  }

  public getDocumentIcon(attachment: DocumentViewModel): string {
    if (this.attachmentType === DocumentTypes.Image) {
      return `<img src="${this.documentUrl(attachment)}" style="width: 100%; height: 100%; object-fit: contain;" />`;
    } else {
      return AttachmentsUtilities.getDocumentIcon(
        AttachmentsUtilities.getDocumentType(attachment).type,
        AttachmentsUtilities.getDocumentType(attachment).ext
      );
    }
  }

  public documentUrl(document: DocumentViewModel): string {
    if (document) {
      let resourseUrl = document.filename;
      if (resourseUrl) {
        const accountId = this.getAccountId();
        resourseUrl = resourseUrl.toLowerCase().startsWith(this.configuration.ResourceFolderName.toLowerCase())
          ? resourseUrl
          : `/${this.configuration.ResourceFolderName}/${accountId}/${resourseUrl}`;
      }
      return document.uploadType === UploadTypes.File ? this.urlService.buildResourceUrl(resourseUrl) : document.filename;
    }
  }

  private getAccountId(): number {
    const externalSession = this.externalAuthService.getCurrentSession();
    if (externalSession && this.externalAuthService.isAuthenticated()) {
      return externalSession.accountId || 0;
    }
    return this.accountId || this.authenticationService.getCurrentAccount()?.id || 0;
  }

  public get isExternalUser(): boolean {
    return this.externalAuthService.isAuthenticated();
  }

  public onAttachmentClick() {
    if (this.attachmentType === DocumentTypes.Link) {
      this.onAttachmentLinkClicked.next(this.attachment);
    }
  }

  public downloadAttachment(event: Event): void {
    event.stopPropagation();
    this.onAttachmentDownload.emit(this.attachment);
  }

  public deleteAttachment(event: Event): void {
    event.stopPropagation();
    this.onaAttachmentDelete.emit(this.attachment);
  }
}
